import {
  formatTradeField,
  ROW2_FIELDS,
  DETAIL_FIELDS,
  CLOSED_DETAIL_FIELDS,
  EDITABLE_CARD_FIELDS,
  rawFieldValue,
  type TradeFieldKey,
  type TradeLike,
} from '@/lib/tradeCardFields'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { CLOSE_ACTIONS } from '@/lib/constants'

export interface TradeCardProps {
  trade: TradeLike & { id: string }
  expanded: boolean
  onToggle: () => void
  onClose: () => void
  onDelete: () => void
  /**
   * When provided, editable detail fields (premium, option price, dates, qty, etc.)
   * render as tap-to-edit inputs that call this with the real Trade model field name.
   * When omitted, the card is fully read-only (keeps it easy to render in tests).
   */
  onEdit?: (field: string, value: string | number) => Promise<void> | void
}

function toneCls(tone?: string): string {
  return tone === 'pos'
    ? 'text-[var(--color-green)]'
    : tone === 'neg'
      ? 'text-[var(--color-red)]'
      : tone === 'muted'
        ? 'text-[var(--color-text-muted)]'
        : 'text-[var(--color-text-primary)]'
}

/**
 * Mobile (phone) trade card: collapsed Row1/Row2, tap to expand full detail + actions.
 * Pure presentational — expand state and handlers are owned by the parent (TradePage),
 * which keeps it easy to unit/render-test in isolation.
 */
export function TradeCard({ trade, expanded, onToggle, onClose, onDelete, onEdit }: TradeCardProps) {
  const isAssign = trade.closeAction === 'Assigned'
  const row2 = ROW2_FIELDS.map((k) => formatTradeField(trade, k))
  const netPnl = formatTradeField(trade, 'netPnl')
  const ret = formatTradeField(trade, 'return')
  const detailKeys: TradeFieldKey[] = [
    ...DETAIL_FIELDS,
    ...(trade.status !== 'open' ? CLOSED_DETAIL_FIELDS : []),
  ]

  return (
    <div className="border border-[var(--color-border)] rounded-xl bg-white overflow-hidden" data-testid={`trade-card-${trade.id}`}>
      <button onClick={onToggle} aria-expanded={expanded} className="w-full text-left px-3 py-3">
        {/* Row 1 */}
        <div className="flex items-center gap-2">
          <span className="font-bold text-[var(--color-accent)] text-base">{trade.ticker}</span>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]">
            {trade.action}
          </span>
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
              isAssign
                ? 'bg-amber-100 text-amber-800'
                : trade.status === 'open'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-slate-100 text-slate-600'
            }`}
          >
            {isAssign ? 'assignment' : trade.status}
          </span>
          <span className="ml-auto text-right">
            <span className={`block text-base font-bold ${toneCls(netPnl.tone)}`}>{netPnl.value}</span>
            <span className={`block text-[11px] ${toneCls(ret.tone)}`}>{ret.value}</span>
          </span>
          <span className="text-[var(--color-text-muted)] text-xs ml-1">{expanded ? '▾' : '▸'}</span>
        </div>
        {/* Row 2 */}
        <div className="flex gap-5 mt-2">
          {row2.map((f) => (
            <span key={f.key} className="text-[13px] text-[var(--color-text-secondary)]">
              <span className="block text-[10px] text-[var(--color-text-muted)]">{f.label}</span>
              {f.value}
            </span>
          ))}
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 border-t border-dashed border-[var(--color-border)]">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-3" onClick={(e) => e.stopPropagation()}>
            {detailKeys.map((key) => {
              const f = formatTradeField(trade, key)
              const editable = onEdit ? EDITABLE_CARD_FIELDS[key] : undefined
              return (
                <div key={key}>
                  <span className="block text-[10px] text-[var(--color-text-muted)]">{f.label}</span>
                  {editable ? (
                    <InlineEdit
                      value={rawFieldValue(trade, key)}
                      fieldName={editable.modelField}
                      type={editable.type}
                      options={editable.type === 'select' ? [...CLOSE_ACTIONS] : undefined}
                      onSave={(v) => onEdit!(editable.modelField, v)}
                      displayFormatter={() => f.value}
                      className={`text-sm font-medium ${toneCls(f.tone)}`}
                    />
                  ) : (
                    <span className={`text-sm font-medium ${toneCls(f.tone)}`}>{f.value}</span>
                  )}
                </div>
              )
            })}
          </div>
          <div className="flex gap-2 mt-3">
            {trade.status === 'open' && (
              <button onClick={onClose} className="flex-1 h-11 rounded-lg bg-green-100 text-green-700 font-semibold text-sm">
                Close
              </button>
            )}
            <button
              onClick={onDelete}
              className="flex-1 h-11 rounded-lg bg-red-50 text-[var(--color-red)] font-semibold text-sm border border-red-100"
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
